import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, IconButton, InputAdornment, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, Alert, alpha, Switch, Divider
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add, Delete, Search, Refresh, Block, Assignment, PersonAdd
} from '@mui/icons-material';
import axios from 'axios';
import ClienteRapidoDialog from '../components/ClienteRapidoDialog';
import ProductoBuscadorDialog from '../components/ProductoBuscadorDialog';

const MOTIVOS = [
  'Devolución de mercadería defectuosa',
  'Error en precio',
  'Error en cantidad',
  'Mercadería en mal estado',
  'Incumplimiento de condiciones',
  'Otro',
];

const initialDetalle = {
  producto_id: '', producto_obj: null,
  descripcion: '', cantidad: 1, unidad_medida: 'NIU',
  costo_unitario: 0, igv_porcentaje: 18,
  igv_monto: 0, subtotal: 0, total: 0,
};

const initialForm = {
  serie: 'NCF', numero: '',
  fecha: new Date().toLocaleDateString('en-CA'),
  proveedor_id: '', proveedor_obj: null,
  compra_id: null, compra_obj: null,
  motivo: '',
  moneda: 'PEN', tipo_cambio: '',
  subtotal: 0, total_igv: 0, total: 0,
  observaciones: '',
  almacen_id: '', almacen_obj: null,
  sucursal_id: '',
  afecta_kardex: true,
  detalles: [{ ...initialDetalle }],
};

function calcDetalle(det) {
  const cantidad = parseFloat(det.cantidad) || 0;
  const costo    = parseFloat(det.costo_unitario) || 0;
  const igvPct   = parseFloat(det.igv_porcentaje) || 0;
  const subtotal = parseFloat((cantidad * costo).toFixed(2));
  const igv_monto = parseFloat((subtotal * igvPct / 100).toFixed(2));
  const total = parseFloat((subtotal + igv_monto).toFixed(2));
  return { ...det, igv_monto, subtotal, total };
}

function calcTotales(detalles) {
  const subtotal  = detalles.reduce((s, d) => s + (parseFloat(d.subtotal)   || 0), 0);
  const total_igv = detalles.reduce((s, d) => s + (parseFloat(d.igv_monto)  || 0), 0);
  return {
    subtotal:  parseFloat(subtotal.toFixed(2)),
    total_igv: parseFloat(total_igv.toFixed(2)),
    total:     parseFloat((subtotal + total_igv).toFixed(2)),
  };
}

function fmt2(n) { return (parseFloat(n) || 0).toFixed(2); }

const SectionHeader = ({ children }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 0.5,
    borderLeft: '3px solid', borderColor: 'error.main',
    pl: 1.5, py: 0.25, mb: 1.5,
    bgcolor: t => alpha(t.palette.error.main, 0.06),
    borderRadius: '0 4px 4px 0',
  }}>
    <Typography variant="subtitle2" color="error.dark" fontWeight={700}>{children}</Typography>
  </Box>
);

export default function NotaCreditoCompraPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');

  const [open, setOpen]     = useState(false);
  const [form, setForm]     = useState({ ...initialForm });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const [proveedores, setProveedores]     = useState([]);
  const [provInputValue, setProvInputValue] = useState('');
  const [openNuevoProv, setOpenNuevoProv]   = useState(false);

  const [almacenes, setAlmacenes]   = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [unidades, setUnidades]     = useState([]);

  const [productOptions, setProductOptions]         = useState({});
  const [productInputValues, setProductInputValues] = useState({});
  const debouncedFetchRefs = useRef({});
  const [openBuscadorProducto, setOpenBuscadorProducto] = useState(false);
  const [buscadorRowIdx, setBuscadorRowIdx]           = useState(0);
  const [copiarNombreADesc, setCopiarNombreADesc]     = useState(true);

  const [compraOptions, setCompraOptions]   = useState([]);
  const [compraInputValue, setCompraInputValue] = useState('');

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // ─── Catálogos ──────────────────────────────────────────────────────────────

  const fetchProveedores = useCallback(async () => {
    try {
      const r = await axios.get('/api/orden-compra/proveedores', { headers });
      setProveedores(r.data || []);
    } catch {}
  }, []);

  const fetchAlmacenes  = async () => { try { const r = await axios.get('/api/almacen/mis-almacenes', { headers }); setAlmacenes(r.data || []); } catch {} };
  const fetchSucursales = async () => { try { const r = await axios.get('/api/sucursales/mis-sucursales', { headers }); setSucursales(r.data || []); } catch {} };
  const fetchUnidades   = async () => { try { const r = await axios.get('/api/unidades', { headers }); setUnidades(r.data || []); } catch {} };

  const fetchCompras = useCallback(debounce(async (q) => {
    try {
      const tk = localStorage.getItem('token');
      const r = await axios.get('/api/compras', {
        headers: { Authorization: `Bearer ${tk}` },
        params: { search: q || '', limit: 20 },
      });
      setCompraOptions(Array.isArray(r.data) ? r.data : (r.data?.data || []));
    } catch { setCompraOptions([]); }
  }, 350), []);

  const fetchProductosForRow = useCallback((rowIdx, q) => {
    if (!debouncedFetchRefs.current[rowIdx]) {
      debouncedFetchRefs.current[rowIdx] = debounce(async (s) => {
        try {
          const res = await axios.get('/api/productos', { headers, params: { search: s, limit: 30 } });
          setProductOptions(prev => ({ ...prev, [rowIdx]: res.data?.data || res.data || [] }));
        } catch {
          setProductOptions(prev => ({ ...prev, [rowIdx]: [] }));
        }
      }, 300);
    }
    debouncedFetchRefs.current[rowIdx](q);
  }, []);

  const loadInitialProducts = useCallback(async (rowIdx) => {
    try {
      const res = await axios.get('/api/productos', { headers, params: { search: '', limit: 40 } });
      setProductOptions(prev => ({ ...prev, [rowIdx]: res.data?.data || res.data || [] }));
    } catch {}
  }, []);

  // ─── Listado ────────────────────────────────────────────────────────────────

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/nota-credito-compra', { headers, params: { search } });
      setRows(r.data || []);
    } catch {}
    setLoading(false);
  }, [search]);

  useEffect(() => { cargar(); }, [cargar]);

  // ─── Formulario ─────────────────────────────────────────────────────────────

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleDetalleChange = (idx, field, value) => {
    setForm(prev => {
      const detalles = prev.detalles.map((d, i) => i !== idx ? d : calcDetalle({ ...d, [field]: value }));
      return { ...prev, detalles, ...calcTotales(detalles) };
    });
  };

  const handleProductoSelect = (idx, prod) => {
    if (!prod) return;
    setForm(prev => {
      const detalles = prev.detalles.map((d, i) => {
        if (i !== idx) return d;
        return calcDetalle({
          ...d,
          producto_id: prod.id,
          producto_obj: prod,
          descripcion: copiarNombreADesc ? (prod.nombre || '') : (d.descripcion || prod.nombre || ''),
          unidad_medida: prod.unidad_medida || 'NIU',
          costo_unitario: prod.precio_compra || prod.precio_costo || 0,
        });
      });
      return { ...prev, detalles, ...calcTotales(detalles) };
    });
  };

  const handleBuscadorSelectNCC = (prod) => {
    handleProductoSelect(buscadorRowIdx, prod);
    setProductInputValues(prev => ({ ...prev, [buscadorRowIdx]: prod.nombre || '' }));
  };

  const handleAddDetalle = () => {
    setForm(prev => {
      const newIdx = prev.detalles.length;
      loadInitialProducts(newIdx);
      return { ...prev, detalles: [...prev.detalles, { ...initialDetalle }] };
    });
  };

  const handleRemoveDetalle = (idx) => {
    setForm(prev => {
      const detalles = prev.detalles.length > 1
        ? prev.detalles.filter((_, i) => i !== idx)
        : prev.detalles;
      return { ...prev, detalles, ...calcTotales(detalles) };
    });
  };

  const abrirNuevo = async () => {
    setError('');
    setProvInputValue('');
    setCompraInputValue('');
    debouncedFetchRefs.current = {};
    setProductOptions({});
    setProductInputValues({});
    await Promise.all([fetchProveedores(), fetchAlmacenes(), fetchSucursales(), fetchUnidades()]);
    const det0 = { ...initialDetalle };
    setForm({ ...initialForm, detalles: [det0] });
    loadInitialProducts(0);
    setOpen(true);
  };

  const anular = async (id) => {
    if (!window.confirm('¿Anular esta nota de crédito de compra?')) return;
    try {
      await axios.put(`/api/nota-credito-compra/${id}/anular`, {}, { headers });
      cargar();
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const guardar = async () => {
    setError('');
    if (!form.numero)      { setError('Ingrese el número de NC'); return; }
    if (!form.fecha)       { setError('Ingrese la fecha'); return; }
    if (!form.proveedor_id) { setError('Seleccione un proveedor'); return; }
    if (form.afecta_kardex && !form.almacen_id) { setError('Seleccione el almacén (requerido para kardex)'); return; }
    const detalles = form.detalles.filter(d => d.descripcion || d.producto_id);
    if (!detalles.length)  { setError('Agregue al menos un producto'); return; }
    setSaving(true);
    try {
      await axios.post('/api/nota-credito-compra', { ...form, detalles }, { headers });
      setOpen(false);
      cargar();
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al guardar');
    }
    setSaving(false);
  };

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 2 }}>
      {/* Cabecera */}
      <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1.5, gap: 1, bgcolor: t => alpha(t.palette.error.main, 0.06), borderRadius: 2 }}>
        <Assignment color="error" sx={{ fontSize: 28 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={700} color="error.dark">Notas de Crédito de Compras</Typography>
          <Typography variant="caption" color="text.secondary">Registro de NC de proveedor — afecta kardex y tesorería</Typography>
        </Box>
        <Button variant="contained" color="error" startIcon={<Add />} onClick={abrirNuevo}>Nueva NC</Button>
      </Paper>

      {/* Búsqueda */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField size="small" placeholder="Buscar proveedor, nro..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ flex: '1 1 220px' }}
        />
        <IconButton onClick={cargar} disabled={loading} color="error"><Refresh /></IconButton>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'error.main' }}>
              {['Nro.', 'Fecha', 'Proveedor', 'Compra ref.', 'Motivo', 'Total', 'Kardex', 'Estado', 'Acciones'].map(h => (
                <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay notas de crédito registradas</TableCell></TableRow>
            ) : rows.map((row, i) => (
              <TableRow key={row.id} hover sx={{ bgcolor: i % 2 === 0 ? 'grey.50' : 'white' }}>
                <TableCell sx={{ fontWeight: 600 }}>{row.serie}-{row.numero}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.fecha?.slice(0, 10)}</TableCell>
                <TableCell>{row.proveedor_nombre || '—'}</TableCell>
                <TableCell>{row.compra_referencia || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 180 }}>
                  <Typography variant="caption" noWrap sx={{ display: 'block' }}>{row.motivo || '—'}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'error.dark' }}>
                  {row.moneda === 'USD' ? '$' : 'S/'} {fmt2(row.total)}
                </TableCell>
                <TableCell>
                  <Chip label={row.kardex_registrado ? 'Sí' : 'No'} color={row.kardex_registrado ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell>
                  <Chip label={row.estado} color={row.estado === 'ACTIVO' ? 'success' : 'default'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  {row.estado === 'ACTIVO' && (
                    <Tooltip title="Anular">
                      <IconButton size="small" color="error" onClick={() => anular(row.id)}>
                        <Block fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ─── Dialog Nueva NC ──────────────────────────────────────────────────── */}
      <Dialog open={open} onClose={(_, r) => { if (r !== 'backdropClick') setOpen(false); }}
        maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment />
            <Box>
              <Typography variant="h6" fontWeight={700}>Nueva Nota de Crédito de Compra</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Registre la NC emitida por el proveedor</Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Sección: Documento */}
          <SectionHeader>Datos del Comprobante</SectionHeader>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item sx={{ minWidth: '8em' }}>
              <TextField label="Serie" size="small" fullWidth value={form.serie}
                onChange={e => handleFormChange('serie', e.target.value.toUpperCase())} />
            </Grid>
            <Grid item sx={{ minWidth: '12em' }}>
              <TextField label="Número *" size="small" fullWidth value={form.numero}
                onChange={e => handleFormChange('numero', e.target.value)} />
            </Grid>
            <Grid item sx={{ minWidth: '11em' }}>
              <TextField label="Fecha *" type="date" size="small" fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.fecha}
                onChange={e => handleFormChange('fecha', e.target.value)} />
            </Grid>
            <Grid item sx={{ minWidth: '22em' }}>
              <TextField select label="Motivo" size="small" fullWidth value={form.motivo}
                onChange={e => handleFormChange('motivo', e.target.value)}>
                <MenuItem value="">Sin especificar</MenuItem>
                {MOTIVOS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item sx={{ minWidth: '12em' }}>
              <TextField select label="Moneda" size="small" fullWidth value={form.moneda}
                onChange={e => handleFormChange('moneda', e.target.value)}>
                <MenuItem value="PEN">PEN (Soles)</MenuItem>
                <MenuItem value="USD">USD (Dólares)</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Sección: Proveedor */}
          <SectionHeader>Proveedor</SectionHeader>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item sx={{ minWidth: '32em' }}>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={proveedores}
                  getOptionLabel={o => `${o.numero_documento || ''} - ${o.razon_social || ''}`}
                  filterOptions={(options, { inputValue }) => {
                    const q = inputValue.toLowerCase();
                    if (!q) return options;
                    return options.filter(o =>
                      (o.numero_documento || '').includes(q) ||
                      (o.razon_social || '').toLowerCase().includes(q)
                    );
                  }}
                  value={form.proveedor_obj}
                  inputValue={provInputValue}
                  onOpen={() => { if (proveedores.length === 0) fetchProveedores(); }}
                  onInputChange={(_, v) => setProvInputValue(v)}
                  onChange={(_, v) => {
                    setProvInputValue(v ? `${v.numero_documento || ''} - ${v.razon_social || ''}` : '');
                    handleFormChange('proveedor_id', v?.id || '');
                    handleFormChange('proveedor_obj', v);
                  }}
                  renderInput={p => <TextField {...p} label="Proveedor *" size="small" fullWidth />}
                  noOptionsText={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>No encontrado</span>
                      <Button size="small" startIcon={<PersonAdd />} onClick={() => setOpenNuevoProv(true)}>Registrar</Button>
                    </Box>
                  }
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                />
                <Tooltip title="Registrar nuevo proveedor">
                  <IconButton size="small" color="error" onClick={() => setOpenNuevoProv(true)}><PersonAdd fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item sx={{ minWidth: '30em' }}>
              <Autocomplete
                options={compraOptions}
                getOptionLabel={o => o ? `${o.serie || ''}-${o.numero || ''} | ${o.proveedor_nombre || ''} | S/ ${fmt2(o.total)}` : ''}
                filterOptions={x => x}
                value={form.compra_obj}
                inputValue={compraInputValue}
                onOpen={() => { if (compraOptions.length === 0) fetchCompras(''); }}
                onInputChange={(_, v) => { setCompraInputValue(v); fetchCompras(v); }}
                onChange={(_, v) => {
                  setCompraInputValue(v ? `${v.serie}-${v.numero}` : '');
                  handleFormChange('compra_id', v?.id || null);
                  handleFormChange('compra_obj', v);
                  if (v && !form.proveedor_id) {
                    setProvInputValue(v.proveedor_ruc ? `${v.proveedor_ruc} - ${v.proveedor_nombre}` : v.proveedor_nombre || '');
                    handleFormChange('proveedor_id', v.proveedor_id || '');
                  }
                }}
                renderInput={p => <TextField {...p} label="Compra relacionada (opcional)" size="small" fullWidth
                  placeholder="Buscar por serie-número..." />}
                noOptionsText="Escriba para buscar compras"
                isOptionEqualToValue={(o, v) => o.id === v?.id}
              />
            </Grid>
          </Grid>

          {/* Sección: Logística */}
          <SectionHeader>Logística y Ajustes</SectionHeader>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item sx={{ minWidth: '20em' }}>
              <Autocomplete
                options={almacenes}
                getOptionLabel={o => o.nombre || ''}
                value={form.almacen_obj}
                onChange={(_, v) => { handleFormChange('almacen_id', v?.id || ''); handleFormChange('almacen_obj', v); }}
                renderInput={p => <TextField {...p} label="Almacén" size="small" fullWidth />}
                noOptionsText="Sin almacenes"
                isOptionEqualToValue={(o, v) => o.id === v?.id}
              />
            </Grid>
            <Grid item sx={{ minWidth: '16em' }}>
              <TextField select label="Sucursal" size="small" fullWidth value={form.sucursal_id}
                onChange={e => handleFormChange('sucursal_id', e.target.value)}>
                <MenuItem value="">Sin sucursal</MenuItem>
                {sucursales.map(s => <MenuItem key={s.id} value={s.id}>{s.nombre || `Sucursal ${s.id}`}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item sx={{ minWidth: '24em' }}>
              <TextField label="Observaciones" size="small" fullWidth value={form.observaciones}
                onChange={e => handleFormChange('observaciones', e.target.value)} />
            </Grid>
            <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch checked={form.afecta_kardex} onChange={e => handleFormChange('afecta_kardex', e.target.checked)} color="error" />}
                label={<Typography variant="body2">Afecta kardex (SALIDA)</Typography>}
              />
            </Grid>
          </Grid>

          {/* Sección: Detalle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              borderLeft: '3px solid', borderColor: 'error.main',
              pl: 1.5, py: 0.25, bgcolor: t => alpha(t.palette.error.main, 0.06),
              borderRadius: '0 4px 4px 0', flexGrow: 1,
            }}>
              <Typography variant="subtitle2" color="error.dark" fontWeight={700}>Detalle de Productos</Typography>
            </Box>
            <FormControlLabel
              control={<Checkbox checked={copiarNombreADesc} onChange={e => setCopiarNombreADesc(e.target.checked)} color="secondary" size="small" />}
              label={<Typography variant="caption">Copiar nombre → descripción</Typography>}
              sx={{ ml: 1 }}
            />
            <Button size="small" variant="outlined" color="error" startIcon={<Add />} onClick={handleAddDetalle} sx={{ ml: 1, whiteSpace: 'nowrap' }}>
              Agregar línea
            </Button>
          </Box>

          <TableContainer component={Paper} elevation={0} variant="outlined"
            sx={{ mb: 2, overflowX: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'error.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 220 }}>Producto</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 75 }}>Cant.</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 100 }}>U.M.</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', width: 110 }}>Costo Unit.</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 65 }}>IGV %</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', width: 95 }}>Subtotal</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', width: 95 }}>Total</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 45 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {form.detalles.map((det, idx) => (
                  <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'action.hover' }, bgcolor: idx % 2 === 0 ? 'grey.50' : 'white' }}>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        options={productOptions[idx] || []}
                        filterOptions={x => x}
                        getOptionLabel={o => `${o.codigo ? o.codigo + ' - ' : ''}${o.nombre || ''}`}
                        value={det.producto_obj || null}
                        inputValue={productInputValues[idx] || ''}
                        onOpen={() => { if (!productOptions[idx]?.length) loadInitialProducts(idx); }}
                        onInputChange={(_, v) => {
                          setProductInputValues(prev => ({ ...prev, [idx]: v }));
                          if (v.length === 0 || v.length >= 2) fetchProductosForRow(idx, v);
                        }}
                        onChange={(_, v) => handleProductoSelect(idx, v)}
                        renderInput={p => <TextField {...p} placeholder="Buscar producto..." />}
                        isOptionEqualToValue={(o, v) => o.id === v?.id}
                        noOptionsText="No encontrado"
                        sx={{ minWidth: 200 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" fullWidth value={det.descripcion}
                        onChange={e => handleDetalleChange(idx, 'descripcion', e.target.value)}
                        onKeyDown={e => { if (e.key === 'F1') { e.preventDefault(); setBuscadorRowIdx(idx); setOpenBuscadorProducto(true); } }}
                        InputProps={{
                          endAdornment: (
                            <Tooltip title="Buscar producto (F1)">
                              <IconButton size="small" onClick={() => { setBuscadorRowIdx(idx); setOpenBuscadorProducto(true); }} edge="end">
                                <Search fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" inputProps={{ min: 0, step: 'any' }} sx={{ width: 70 }}
                        value={det.cantidad}
                        onChange={e => handleDetalleChange(idx, 'cantidad', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        options={unidades}
                        getOptionLabel={u => u.abreviatura || ''}
                        value={unidades.find(u => u.abreviatura === det.unidad_medida) || null}
                        onChange={(_, v) => handleDetalleChange(idx, 'unidad_medida', v?.abreviatura || '')}
                        renderInput={p => <TextField {...p} placeholder="UM" />}
                        freeSolo
                        sx={{ width: 95 }}
                        isOptionEqualToValue={(o, v) => o.abreviatura === v?.abreviatura}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" inputProps={{ min: 0, step: 'any' }} sx={{ width: 100 }}
                        value={det.costo_unitario}
                        onChange={e => handleDetalleChange(idx, 'costo_unitario', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" inputProps={{ min: 0, step: 'any' }} sx={{ width: 60 }}
                        value={det.igv_porcentaje}
                        onChange={e => handleDetalleChange(idx, 'igv_porcentaje', e.target.value)} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {fmt2(det.subtotal)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'error.dark', whiteSpace: 'nowrap' }}>
                      {fmt2(det.total)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleRemoveDetalle(idx)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totales */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Paper variant="outlined" sx={{ p: 1.5, minWidth: 220 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                <Typography variant="body2" fontWeight={600}>S/ {fmt2(form.subtotal)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">IGV (18%):</Typography>
                <Typography variant="body2" fontWeight={600}>S/ {fmt2(form.total_igv)}</Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight={700} color="error.dark">TOTAL NC:</Typography>
                <Typography variant="body1" fontWeight={800} color="error.dark">S/ {fmt2(form.total)}</Typography>
              </Box>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)} variant="outlined">Cancelar</Button>
          <Button variant="contained" color="error" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <Assignment />}
            onClick={guardar}>
            Registrar NC
          </Button>
        </DialogActions>
      </Dialog>

      <ClienteRapidoDialog
        open={openNuevoProv}
        onClose={() => setOpenNuevoProv(false)}
        tipo="Proveedor"
        onCreated={(prov) => {
          setOpenNuevoProv(false);
          setProvInputValue(`${prov.numero_documento || ''} - ${prov.razon_social || ''}`);
          handleFormChange('proveedor_id', prov.id);
          handleFormChange('proveedor_obj', prov);
          fetchProveedores();
        }}
      />
      <ProductoBuscadorDialog
        open={openBuscadorProducto}
        onClose={() => setOpenBuscadorProducto(false)}
        onSelect={handleBuscadorSelectNCC}
      />
    </Box>
  );
}
